import { apiClient } from './client';
import {
  ProjectFilesResponse,
  FileUploadRequest,
  FileUploadResponse,
  FileDownloadResponse,
  FileProcessResponse,
  ProcessingStatusResponse,
  LLMResultsResponse,
  APIResponse,
} from '../types/api';

export const filesAPI = {
  // Get all files for a project
  getProjectFiles: async (projectId: string): Promise<ProjectFilesResponse> => {
    return apiClient.get(`/files/projects/${projectId}`);
  },

  // Request file upload (get presigned URL)
  requestFileUpload: async (
    projectId: string,
    data: FileUploadRequest
  ): Promise<FileUploadResponse> => {
    return apiClient.post(`/files/projects/${projectId}`, data);
  },

  // Upload file to S3 using presigned URL
  uploadFileToS3: async (
    uploadUrl: string,
    file: File,
    onProgress?: (progress: number) => void
  ): Promise<void> => {
    return apiClient.uploadFile(uploadUrl, file, onProgress);
  },

  // Get file download URL
  getFileDownload: async (fileId: string): Promise<FileDownloadResponse> => {
    return apiClient.get(`/files/${fileId}/download`);
  },

  // Delete a file
  deleteFile: async (fileId: string): Promise<APIResponse> => {
    return apiClient.delete(`/files/${fileId}`);
  },

  // Process a file
  processFile: async (fileId: string): Promise<FileProcessResponse> => {
    return apiClient.post(`/files/${fileId}/process`);
  },

  // Get processing status
  getProcessingStatus: async (fileId: string): Promise<ProcessingStatusResponse> => {
    return apiClient.get(`/files/${fileId}/processing-status`);
  },

  // Get LLM results
  getLLMResults: async (fileId: string): Promise<LLMResultsResponse> => {
    return apiClient.get(`/files/${fileId}/llm-results`);
  },

  // Complete file upload flow (new format)
  uploadFile: async (
    projectId: string,
    fileData: FileUploadRequest,
    autoProcess: boolean = false
  ): Promise<FileUploadResponse> => {
    const endpoint = autoProcess 
      ? `/files/projects/${projectId}/auto-process`
      : `/files/projects/${projectId}`;
    
    return apiClient.post(endpoint, fileData);
  },

  // Legacy file upload flow (keep for backward compatibility)
  uploadFileLegacy: async (
    projectId: string,
    file: File,
    onProgress?: (progress: number) => void
  ): Promise<{
    success: boolean;
    file?: any;
    error?: string;
  }> => {
    try {
      // Step 1: Request upload URL
      const uploadResponse = await filesAPI.requestFileUpload(projectId, {
        filename: file.name,
        mimeType: file.type,
        fileSize: file.size,
      });

      if (!uploadResponse.success || !uploadResponse.data) {
        return {
          success: false,
          error: uploadResponse.message || 'Failed to get upload URL',
        };
      }

      const { uploadUrl, file: fileRecord } = uploadResponse.data;

      // Step 2: Upload file to S3
      await filesAPI.uploadFileToS3(uploadUrl, file, onProgress);

      return {
        success: true,
        file: fileRecord,
      };
    } catch (error: any) {
      return {
        success: false,
        error: error.message || 'Upload failed',
      };
    }
  },
};